import { useContext, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import Card from "../../ui/Card";
import { t } from "i18next";
import { CircleDot, TrendingUp } from "lucide-react";
import { FormControl, MenuItem, Select, Tooltip as MuiTooltip } from "@mui/material";
import { AuthContext } from "../../../context/AuthContext";
import { useSupplierInsightContext } from "../../../pages/SupplierInsights/SupplierInsightContext";
const CustomYAxisLabel = ({ viewBox, value }) => {
  const lines = value.split("\n");
  const { x, y, height } = viewBox;

  const lineHeight = 14; // estimated line height in px
  const totalTextHeight = lineHeight * lines.length;
  const centerY = y + height / 2; // vertical center of Y-axis
  const startDy = -((totalTextHeight - lineHeight) / 2); // to center the multiline block

  return (
    <text
      x={x}
      y={centerY}
      textAnchor="middle"
      transform={`rotate(-90, ${x}, ${centerY})`}
      fill="#666"
    >
      {lines.map((line, index) => (
        <tspan key={index} x={x} dy={index === 0 ? startDy : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

function SupplierContamination({ currentSupplier, onSupplierClick, viewMode, onViewModeChange }) {
  const { allSupplierDataFiltered, allSuppliers } = useSupplierInsightContext();
  const [selectedContaminant, setSelectedContaminant] = useState("");
  const [hoveredDot, setHoveredDot] = useState(null);

  const { labelsets } = useContext(AuthContext);

  const { processedData, contaminantsList, currentSupplierData } =
    useMemo(() => {
      if (allSuppliers.length === 0) return [];

      let processedData = [];
      let contaminantsList = [];
      let currentSupplierData = [];

      allSuppliers.forEach((supplier) => {
        const supplierData = allSupplierDataFiltered[supplier.id];
        const total_count = supplierData?.aggregatedObservations?.total_count || 0;
        const contaminants =
          supplierData?.aggregatedObservations?.data_per_labelset?.contaminants || {};
        let data = {};
        if (Object.keys(contaminants).length > 0) {
          Object.entries(contaminants).forEach(([key, value]) => {
            if (!contaminantsList.includes(key)) {
              contaminantsList.push(key);
            }
            data[key] = {
              contamination_percentage:
                total_count > 0 ? (value.count / total_count) * 100 : 0, // x axis
              contamination_foils_per_bale:
                total_count > 0 ? value.detection_count / total_count : 0, // y axis qty
              contamination_foils_per_contaminated_bale:
                value.count > 0 ? value.detection_count / value.count : 0, // y axis contaminated qty
              contamination_area_percentage:
                total_count > 0
                  ? (value.detection_area_frac_sum / total_count) * 100
                  : 0, // y axis area
              detection_count: value.detection_count,
              count: value.count,
            };
          });
          // Always include the supplier if there is at least one contaminant in the list
          if (supplier.id === currentSupplier.id) {
            currentSupplierData.push({
              display_name: supplier.display_name,
              id: supplier.id,
              data,
              total_count: total_count,
            });
          } else {
            processedData.push({
              display_name: supplier.display_name,
              id: supplier.id,
              data,
              total_count: total_count,
            });
          }
        } else if (total_count > 0) {
          // No contaminants for this supplier, but still include with zeros only if total_count > 0
          contaminantsList.forEach((key) => {
            data[key] = {
              contamination_percentage: 0,
              contamination_foils_per_bale: 0,
              contamination_foils_per_contaminated_bale: 0,
              contamination_area_percentage: 0,
              detection_count: 0,
              count: 0,
            };
          });
          if (supplier.id === currentSupplier.id) {
            currentSupplierData.push({
              display_name: supplier.display_name,
              id: supplier.id,
              data,
              total_count: total_count,
            });
          } else {
            processedData.push({
              display_name: supplier.display_name,
              id: supplier.id,
              data,
              total_count: total_count,
            });
          }
        }
      });

      return { processedData, contaminantsList, currentSupplierData };
    }, [allSupplierDataFiltered, allSuppliers]);

  useEffect(() => {
    if (
      contaminantsList &&
      contaminantsList.length > 0 &&
      selectedContaminant === ""
    ) {
      setSelectedContaminant(contaminantsList[0]);
    }
  }, [contaminantsList]);

  const getContaminantLabel = (contaminant) => {
    return (
      labelsets?.contaminants?.labels?.[contaminant]?.full_name ?? contaminant
    );
  };
  const selectedContaminantLabel = getContaminantLabel(selectedContaminant);

  const handleSupplierClick = (data) => {
    // Clear hover state immediately on click to prevent it from getting stuck
    setHoveredDot(null);
    
    // Validate the data structure from Recharts
    if (!data) {
      console.warn('No data received from chart click');
      return;
    }

    // Recharts can pass data in different structures depending on the chart type
    // Try to extract supplier ID from common data structures
    const supplierId = data.payload?.id || data.id;
    
    if (!supplierId) {
      console.warn('Invalid supplier data from chart click - no ID found:', data);
      return;
    }

    // Validate that onSupplierClick callback exists
    if (!onSupplierClick) {
      console.warn('No onSupplierClick callback provided');
      return;
    }

    // Additional validation: ensure the supplier exists in our data
    const supplierExists = allSuppliers.some(supplier => supplier.id === supplierId);
    if (!supplierExists) {
      console.error('Clicked supplier not found in supplier list:', supplierId);
      return;
    }

    try {
      onSupplierClick(supplierId, data);
    } catch (error) {
      console.error('Error handling supplier click:', error);
    }
  };

  // Clear hover state when current supplier changes (defensive cleanup)
  useEffect(() => {
    setHoveredDot(null);
  }, [currentSupplier?.id]);

  // Clear hover state when view mode changes (defensive cleanup)
  useEffect(() => {
    setHoveredDot(null);
  }, [viewMode, selectedContaminant]);

  const data_label = useMemo(() => {
    let data_label = {
      x_axis_data_key: `data.${selectedContaminant}.contamination_percentage`,
      x_axis_label: t("contaminated_bales_in_percentage"),
      y_axis_data_key: `data.${selectedContaminant}.contamination_foils_per_bale`,
      y_axis_label: `${t("contaminant_count")}`,
    };

    if (viewMode === "area") {
      data_label.y_axis_data_key = `data.${selectedContaminant}.contamination_area_percentage`;
      data_label.y_axis_label = t("contaminated_area_per_bale");
    }
    if (viewMode === "level") {
      data_label.y_axis_data_key = `data.${selectedContaminant}.contamination_foils_per_contaminated_bale`;
      data_label.y_axis_label = t("contaminated_qty_label", {
        x: selectedContaminant,
      });
    }

    return data_label;
  }, [selectedContaminant, viewMode]);

  if (!contaminantsList || contaminantsList?.length === 0) return null;

  return (
    <Card className="mb-5">
      <p className="m-0">{t("supplier_contamination_analysis")}</p>
      <div className="flex items-center gap-4 mt-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">{t("contaminant")}:</label>
          <FormControl fullWidth>
            <Select
              value={selectedContaminant}
              onChange={(e) => setSelectedContaminant(e.target.value)}
              className="cst-select"
            >
              {contaminantsList.length === 0 ? (
                <MenuItem disabled>No contaminants available</MenuItem>
              ) : (
                contaminantsList.map((contaminant) => (
                  <MenuItem key={contaminant} value={contaminant}>
                    {getContaminantLabel(contaminant)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <MuiTooltip title={t("tooltip_contaminated_qty")} arrow>
            <button
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-150 border-cyan-500 cursor-pointer ${
                viewMode === "level"
                  ? "bg-cyan-500 text-white"
                  : "bg-white text-cyan-500"
              }`}
              onClick={() => onViewModeChange("level")}
            >
              <CircleDot className="h-3.5 w-3.5" /> {t("contaminated_qty")}
            </button>
          </MuiTooltip>
          <MuiTooltip title={t("tooltip_qty")} arrow>
            <button
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-150 border-blue-500 cursor-pointer ${
                viewMode === "qty"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-blue-500"
              }`}
              onClick={() => onViewModeChange("qty")}
            >
              <CircleDot className="h-3.5 w-3.5" /> {t("qty")}
            </button>
          </MuiTooltip>
          <MuiTooltip title={t("tooltip_area")} arrow>
            <button
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-150 border-green-500 bg-green-500 cursor-pointer ${
                viewMode === "area"
                  ? "bg-green text-white"
                  : "bg-white text-green-500"
              }`}
              onClick={() => onViewModeChange("area")}
            >
              <TrendingUp className="h-3.5 w-3.5" /> {t("area")}
            </button>
          </MuiTooltip>
        </div>
      </div>
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              type="number"
              dataKey={data_label.x_axis_data_key}
              unit="%"
              // domain={[0, 100]}
              tickFormatter={(value) => `${value}`}
              label={{
                value: data_label.x_axis_label,
                position: "insideBottom",
                offset: -15,
                style: { textAnchor: "middle" },
              }}
            />
            <YAxis
              type="number"
              dataKey={data_label.y_axis_data_key}
              tickFormatter={(value) =>
                viewMode === "area" ? `${value.toFixed(1)}%` : value.toFixed(2)
              }
              label={{  
                value: data_label.y_axis_label,
                content: <CustomYAxisLabel />,
              }}
              tickCount={10}
            />
            <ZAxis
              type="number"
              dataKey="totalBales"
              range={[40, 100]}
              name="Total Bales"
            />
            <Tooltip
              wrapperStyle={{ pointerEvents: "auto" }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  const _data = data.data[selectedContaminant];
                  return (
                    <div className="bg-white p-3 border border-gray-100 rounded-md shadow-md text-sm">
                      <p className="font-medium m-0">{data.display_name}</p>
                      <div className="text-sm space-y-1 mt-1.5">
                        <p className="text-gray-400 flex justify-between my-1 gpa-2">
                          <span>{t("contaminated_bales")}:</span>
                          <span className="ml-2 font-medium text-black">
                            <strong>{_data.count.toFixed(0)}</strong>{" "}
                            {t("contaminated_bales_percentage", {
                              y: data.total_count.toFixed(0),
                              z: _data.contamination_percentage.toFixed(2),
                            })}
                          </span>
                        </p>
                        <p className="text-gray-400 flex justify-between my-1 gpa-2">
                          <span>{t("contaminated_area")}:</span>
                          <span className="ml-2 font-medium text-black">
                            <strong>
                              {_data.contamination_area_percentage.toFixed(1)}%
                            </strong>{" "}
                            {t("x_of_bale_surface")}
                          </span>
                        </p>
                        <p className="text-gray-400 flex justify-between my-1 gpa-2">
                          <span>
                            {t("foils_detected", {
                              contaminant: selectedContaminantLabel,
                            })}{" "}
                            :
                          </span>
                          <span className="ml-2 font-medium text-black">
                            <strong>
                              {_data.contamination_foils_per_bale.toFixed(2)}
                            </strong>{" "}
                            {t("x_per_bale")}
                          </span>
                        </p>
                        <p className="text-gray-400 flex justify-between my-1 gpa-2">
                          <span>
                            {t("contaminations_in_contaminated_bales_tooltip")}:
                          </span>
                          <span className="ml-2 font-medium text-black">
                            <strong>
                              {_data.contamination_foils_per_contaminated_bale.toFixed(
                                2
                              )}
                              {" "}
                              {t("x_per_bale")}
                            </strong>{" "}
                          </span>
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
              // active={true}
            />
            {/* Other suppliers */}
            <Scatter
              data={processedData}
              fill="#94a3b8"
              line={false}
              shape={(props) => {
                const { cx, cy, payload } = props;
                const isHovered = hoveredDot === payload?.id;
                const radius = isHovered ? 8 : 5;
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={radius}
                    fill="#94a3b8"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                  />
                );
              }}
              legendType="circle"
              onClick={handleSupplierClick}
              onMouseEnter={(data) => setHoveredDot(data?.id)}
              onMouseLeave={() => setHoveredDot(null)}
            />
            {currentSupplierData.length > 0 && (
              <Scatter
                data={currentSupplierData}
                fill="#f43f5e"
                line={false}
                shape={(props) => {
                  const { cx, cy, payload } = props;
                  const isHovered = hoveredDot === payload?.id;
                  const radius = isHovered ? 12 : 8;
                  return (
                    <g style={{ cursor: 'pointer' }}>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={radius}
                        stroke="#f43f5e"
                        strokeWidth={2.5}
                        fill="#f43f5e"
                        fillOpacity={0.8}
                        style={{ transition: 'all 0.2s ease' }}
                      />
                      <circle 
                        cx={cx} 
                        cy={cy} 
                        r={isHovered ? 4 : 3} 
                        stroke="none" 
                        fill="#fff"
                        style={{ transition: 'all 0.2s ease' }}
                      />
                    </g>
                  );
                }}
                legendType="circle"
                onClick={handleSupplierClick}
                onMouseEnter={(data) => setHoveredDot(data?.id)}
                onMouseLeave={() => setHoveredDot(null)}
              />
            )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export default SupplierContamination;
